// Two separate facts about a program item, from the program and the video library:
// - status: ownVideoHash, set when the item itself was recorded (its play icon links to it, with
//   no viewing note): the sheet's Video, or the library video the item matches when it's
//   the earliest program item matching it (useShowcaseVideo getVideoOwners), e.g. the April talk
//   and not a November summary or breakout Q&A of it;
// - videos to watch for it (the play icon at the end of the item, and the viewing note): its
//   own recording; else the earlier recordings it builds on: the library video it matches, and
//   for an online-only item each speaker's recorded talk. Each video lists its speakers.
// The viewing note (above the abstract) is for 5-minute summary talks and breakout sessions.
import useShowcaseVideo, {getCanHaveVideo} from "@/composables/useShowcaseVideo";

const SUMMARY_TALK_MS = 5 * 60 * 1000;

function getIsOnlineOnly(event) {
  return /online\s*only/i.test(event.mode || "");
}

// [{name, hash}] in speaker order → [{hash, speakers: [name, …]}], one per video
function groupBySpeakerVideo(pairs) {
  const videos = [];
  for (const {name, hash} of pairs) {
    const video = videos.find((candidate) => candidate.hash === hash);
    if (video) video.speakers.push(name);
    else videos.push({hash, speakers: [name]});
  }
  return videos;
}

function getNoteKind(event) {
  if (/breakout/i.test(event.title || "")) return "breakout session";
  const start = event.start?.toMillis?.();
  const end = event.end?.toMillis?.();
  if (start == null || end == null || end - start !== SUMMARY_TALK_MS) return "";
  return "summary talk";
}

export default function useEventVideos() {
  const {findVideoHash, findSpeakerVideoHash, getVideoMinutes} = useShowcaseVideo();

  function getOwnVideoHash(event, libraryHash, videoOwners) {
    if (event.video || event.Video) return event.video || event.Video;
    if (!libraryHash) return null;
    if (!videoOwners) return libraryHash; // no program-wide view: the match is its own
    return videoOwners.get(libraryHash) === event.ref ? libraryHash : null;
  }

  function getRelatedVideos(event, speakerNames, libraryHash, year) {
    const isOnlineOnly = getIsOnlineOnly(event);
    const pairs = speakerNames.map((name) => {
      const speakerHash = isOnlineOnly ? findSpeakerVideoHash([name], year) : null;
      return {name, hash: speakerHash || libraryHash};
    });
    return groupBySpeakerVideo(pairs.filter((pair) => pair.hash));
  }

  // {ownVideoHash, videos: [{hash, speakers}]}
  function getEventVideos(event, {speakerNames, year, videoOwners}) {
    if (!getCanHaveVideo(event)) return {ownVideoHash: null, videos: []};
    const libraryHash = findVideoHash(speakerNames, event.title, year);
    const ownVideoHash = getOwnVideoHash(event, libraryHash, videoOwners);
    if (ownVideoHash) return {ownVideoHash, videos: [{hash: ownVideoHash, speakers: speakerNames}]};
    return {ownVideoHash: null, videos: getRelatedVideos(event, speakerNames, libraryHash, year)};
  }

  // {kind, videos: [{hash, speakers, minutes}]} for a summary talk or breakout that builds on
  // earlier recordings; null otherwise
  function getViewingNote(event, {ownVideoHash, videos}) {
    if (ownVideoHash || !videos.length) return null;
    const kind = getNoteKind(event);
    if (!kind) return null;
    const withMinutes = videos.map((video) => ({...video, minutes: getVideoMinutes(video.hash)}));
    return {kind, videos: withMinutes};
  }

  return {getEventVideos, getViewingNote};
}
